import { useTranslation } from 'react-i18next';
import { Button, DropdownMenu } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import {
  labelInitialValues,
  LabelTabs,
} from 'components/organisms/forms/label/LabelForm';
import { useComponentVisible } from 'hooks';
import { useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import i18next from 'i18next';
import middleware from 'middleware';
import { PiDotsThreeVerticalBold, PiPencilSimpleLine } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBanLabel, useDeleteLabel, useGetLabel, useGetVcfLabel, useSendUpdateLabelEmail } from 'services/labels';
import { LABEL_STATUS } from 'services/labels/types';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getFinanceStatisticsBackPath } from 'utils/nav/financeStatisticsNavigation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const Label = () => {
  const { t } = useTranslation();
  const labelId = useParamId('label');
  const { isPending, mutateAsync } = useDeleteLabel();
  const { mutate: downloadVcfLabel, isPending: isDownloading } =
    useGetVcfLabel();
  const { mutate: banLabel, isPending: isBanning } = useBanLabel();
  const { mutate: sendUpdateLabelEmail, isPending: isSendingEmail } = useSendUpdateLabelEmail();

  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const hasPermission = useHasPermission();
  const dropdownVisible = useComponentVisible<HTMLDivElement>(false);
  const licenceConfiguration = useGetLicenceConfiguration();

  const { data: label, isLoading, refetch } = useGetLabel(labelId);

  const handleDeleteLabel = async () => {
    await mutateAsync(labelId, {
      onSuccess: () => navigate('/label'),
    });
  };

  const handleBanLabel = async (actions: any, withdrawAllProducts: boolean) => {
    banLabel(
      { labelId, withdrawAllProducts },
      {
        onSuccess: () => {
          actions.close();
          refetch();
        },
        onError: error => onErrorShowAllErrorsToast(error)
      }
    );
  };

  return (
    <Form.Label
      i18nextPrefix='label'
      initialValues={
        label ? middleware.label.queryToForm(label) : labelInitialValues
      }
      onSubmit={() => {}}
      tittle={label?.labelName ?? i18next.t('label.title.individual')}
      backPath={getFinanceStatisticsBackPath('label', locationState)}
      disableTabs={[
        ...(label?.labelStatus === LABEL_STATUS.Draft
          ? [
              LabelTabs.advance,
              LabelTabs.balance,
              LabelTabs.contracts,
              LabelTabs.defaultContracts,
              LabelTabs.reports,
              LabelTabs.settlements,
              LabelTabs.statistics,
              LabelTabs.threads,
            ]
          : []),
      ]}
      status={{ displayMode: true, isLoading }}
      topbarContent={
        <div className='flex gap-2 items-center'>
          {hasPermission('LabelPermissionsLIST**', 2) && label?.labelStatus !== LABEL_STATUS.Banned && (
            <Button
              isClear
              onlyIcon
              onClick={() => {
                label &&
                  navigate(`/label/edit/${labelId}/finances`, {
                    replace: true,
                  });
              }}>
              <Button.Description description={t('button.edit')} />

              <Button.Icon icon={<PiPencilSimpleLine />} />
            </Button>
          )}
          {hasPermission('LabelPermissionsLIST**', 4) && (
            <MagellanPopups.DeleteWarning
              onRemove={handleDeleteLabel}
              removeLoading={isPending}
              name={label?.labelName}
            />
          )}
          <DropdownMenu
            ref={dropdownVisible.ref}
            visible={dropdownVisible.isComponentVisible}
            style={{ padding: 5, top: "25%" }}
            justifyPosition={'end'}>
            <div className='flex flex-col w-full'>
              <Button
                isClear
                fullWidth
                onClick={() => downloadVcfLabel(labelId)}
                disabled={isDownloading}>
                <Button.Text text={i18next.t('button.download-contacts')} />
              </Button>
              {label?.labelStatus === LABEL_STATUS.Draft && (
                <Button
                  isClear
                  fullWidth
                  onClick={() => sendUpdateLabelEmail(labelId)}
                  disabled={isSendingEmail}>
                  <Button.Text text={i18next.t('button.send-update-label-email')} />
                </Button>
              )}
              {label?.labelStatus !== LABEL_STATUS.Banned &&
                (licenceConfiguration.data?.clientModuleAccess ?? false) && (
                  <MagellanPopups.BanLabelWarning
                    trigger={
                      <Button isClear fullWidth disabled={isBanning}>
                        <Button.Text text={i18next.t('button.ban-label')} />
                      </Button>
                    }
                    name={label?.labelName}
                    transI18nKey={'label.ban-warning'}
                    onBan={handleBanLabel}
                    banLoading={isBanning}
                  />
                )}
            </div>
          </DropdownMenu>
          <Button
            variant={BUTTON_VARIANT.default}
            onlyIcon
            isClear
            onClick={dropdownVisible.handleComponentVisible}>
            <Button.Description description={t('button.more-options')} />
            <Button.Icon icon={<PiDotsThreeVerticalBold />} />
          </Button>
        </div>
      }
    />
  );
};

export default Label;
